import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, ArrowRight, MonitorSmartphone } from 'lucide-react';
import kabbiLogo from '@/assets/images/kabbi-logo-gelb.png';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { loginSchema, type LoginInput } from '@kabbi/shared/schemas/forms';
import { zodFormResolver } from '@/lib/zodResolver';
import { firstErrorMessage } from '@/lib/formErrors';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import AuthMapPanel from '@/components/AuthMapPanel';
import { useAuthStore } from '../store/authStore';
import { usePreferencesStore } from '../store/preferencesStore';
import { useThemeStore, type Theme } from '../store/themeStore';
import i18n from '../i18n';

// Hoisted out of the component so the resolver is built once per module
// load, not once per render. RHF reads `options.resolver` on mount, so the
// per-render allocation is otherwise wasted.
const loginResolver = zodFormResolver(loginSchema);

/**
 * Login page.
 *
 * Two-column layout: a frosted-glass sign-in card on a soft gradient panel
 * (left), and our decorative Bamberg streetmap SVG filling the panel on the
 * right (hidden below `md`). Colours come from the active theme tokens.
 *
 * Auth wiring is unchanged: RHF + zod validation against the shared
 * `loginSchema`, the auth store handles the POST, and a 409
 * OTHER_SESSION_ACTIVE response opens the force-login AlertDialog. Errors
 * surface via sonner; invalid fields get a red border via `aria-invalid`.
 */
export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuthStore();
  const { fetch: fetchPrefs } = usePreferencesStore();
  const { t } = useTranslation();
  const currentLang = i18n.language;

  const idleReason = searchParams.get('reason') === 'idle';

  // RHF form. defaultValues are required so the inputs stay controlled
  // through the demo-fill button's `setValue` calls.
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: loginResolver,
    defaultValues: { email: '', password: '' },
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // When the API returns 409 OTHER_SESSION_ACTIVE we open this confirmation
  // dialog. `activeSessions` carries the count from the server so the prompt
  // can mention "1 other session" vs "2 other sessions".
  const [otherSessionPrompt, setOtherSessionPrompt] = useState<{
    activeSessions: number;
  } | null>(null);

  useEffect(() => {
    if (idleReason) toast.info(t('login.idleLogout'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Switch the active i18n language and persist the choice for next visit. */
  const switchLang = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('kabbi_lang', lang);
  };

  /**
   * Run the post-login bootstrap (preferences fetch + theme/locale apply +
   * navigate to dashboard). Shared by the first-attempt success path AND
   * the force-retry success path so the two stay in sync.
   */
  const finishLogin = async () => {
    await fetchPrefs();
    const { getPref } = usePreferencesStore.getState();
    const savedTheme = getPref<string>('theme');
    if (savedTheme) useThemeStore.getState().setTheme(savedTheme as Theme);
    const savedLocale = getPref<string>('locale');
    if (savedLocale) { i18n.changeLanguage(savedLocale); localStorage.setItem('kabbi_lang', savedLocale); }
    navigate('/dashboard');
  };

  /**
   * Map a thrown login error to the right toast or AlertDialog. Pulled out
   * of onSubmit so the force-retry path (after the user confirms in
   * the AlertDialog) can reuse the same handling.
   */
  const handleLoginError = (err: unknown) => {
    const code = (err as { code?: string })?.code;
    const activeSessions = (err as { activeSessions?: number })?.activeSessions ?? 0;
    if (code === 'OTHER_SESSION_ACTIVE') {
      setOtherSessionPrompt({ activeSessions });
      return;
    }
    if (code === 'ACCOUNT_INACTIVE')         toast.error(t('login.errorInactive'));
    else if (code === 'ACCOUNT_SUSPENDED')   toast.error(t('login.errorSuspended'));
    else if (code === 'EMAIL_NOT_VERIFIED')  toast.error(t('login.errorEmailNotVerified'));
    else                                     toast.error(t('login.error'));
  };

  /**
   * Submit handler invoked by RHF after schema validation passes. Calls the
   * auth store, runs the post-login bootstrap, and maps thrown errors to
   * toasts / AlertDialog state.
   */
  const onSubmit = async (data: LoginInput) => {
    setLoading(true);
    try {
      await login(data.email, data.password);
      await finishLogin();
    } catch (err) {
      handleLoginError(err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Validation-failed handler. Surfaces the first invalid field's i18n key
   * via toast — the per-field red border is rendered through `aria-invalid`.
   */
  const onInvalid = (errs: typeof errors) => {
    const message = firstErrorMessage(errs);
    if (message) toast.error(t(message));
  };

  /**
   * Called when the user confirms in the "another session is active"
   * dialog. Re-runs login with force=true; the API drops the existing
   * sessions and issues a fresh JWT here.
   */
  const handleForceLogin = async () => {
    setOtherSessionPrompt(null);
    setLoading(true);
    const { email, password } = getValues();
    try {
      await login(email, password, true);
      await finishLogin();
    } catch (err) {
      handleLoginError(err);
    } finally {
      setLoading(false);
    }
  };

  /** Dev-only one-click prefill for the form fields. */
  const fillDemo = (e: React.MouseEvent, demoEmail: string) => {
    e.preventDefault();
    setValue('email', demoEmail, { shouldValidate: false });
    setValue('password', 'password123', { shouldValidate: false });
  };

  /** Language toggle button — active uses the navy primary, idle is muted. */
  const LangButton = ({ code, label }: { code: string; label: string }) => (
    <button
      type="button"
      onClick={() => switchLang(code)}
      aria-current={currentLang === code ? 'true' : undefined}
      className={`rounded-lg px-2.5 py-1 text-[11.5px] font-semibold transition-colors ${
        currentLang === code
          ? 'bg-primary text-primary-foreground'
          : 'bg-card/70 text-muted-foreground hover:text-primary'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex h-svh w-full overflow-hidden bg-muted">
      {/* ── LEFT: sign-in card on a soft gradient panel ── */}
      <div className="relative flex w-full flex-col items-center justify-center bg-gradient-to-br from-secondary to-muted px-8 py-10 md:w-[44%] md:shrink-0">
        {/* Language switcher, top-left */}
        <div className="absolute left-8 top-6 flex gap-1">
          <LangButton code="de" label="DE" />
          <LangButton code="en" label="EN" />
        </div>

        {/* Frosted-glass card */}
        <section
          className="w-full max-w-[418px] rounded-3xl border border-border/60 bg-card/90 p-2.5 shadow-2xl backdrop-blur-xl"
          aria-labelledby="login-title"
        >
          <img src={kabbiLogo} alt="Kabbi" className="mb-9 h-9 w-auto" />

          <h1 id="login-title" className="mb-1.5 text-2xl font-bold tracking-tight text-foreground">
            {t('login.welcome')}
          </h1>
          <p className="mb-8 text-sm text-muted-foreground">{t('login.subtitle')}</p>

          <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="flex flex-col gap-4" noValidate>
            {/* Email */}
            <div className="flex flex-col">
              <Label
                htmlFor="email"
                className="mb-1.5 block text-[11.5px] font-semibold uppercase tracking-[0.03em] text-muted-foreground"
              >
                {t('login.email')}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@kabbi.local"
                autoComplete="email"
                disabled={loading}
                aria-invalid={errors.email ? 'true' : undefined}
                className="h-11 rounded-xl bg-muted text-sm"
                {...register('email')}
              />
            </div>

            {/* Password */}
            <div className="flex flex-col">
              <Label
                htmlFor="password"
                className="mb-1.5 block text-[11.5px] font-semibold uppercase tracking-[0.03em] text-muted-foreground"
              >
                {t('login.password')}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  disabled={loading}
                  aria-invalid={errors.password ? 'true' : undefined}
                  className="h-11 rounded-xl bg-muted pr-11 text-sm"
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
                  tabIndex={-1}
                  aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                >
                  {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              </div>
            </div>

            {/* Submit — brand-yellow primary action */}
            <Button
              type="submit"
              disabled={loading}
              className="mt-2 h-12 w-full gap-2.5 rounded-xl bg-brand text-[15px] font-bold text-brand-foreground shadow-md hover:bg-brand hover:brightness-105"
            >
              {loading ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  {t('login.loading')}
                </>
              ) : (
                <>
                  <span>{t('login.submit')}</span>
                  <ArrowRight className="h-[18px] w-[18px]" strokeWidth={2.5} />
                </>
              )}
            </Button>

            {/* Footer row: compliance note + registration link */}
            <div className="mt-6 flex items-center justify-between border-t border-border pt-4 text-[11.5px] text-muted-foreground">
              <span className="inline-flex items-center gap-2">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand" aria-hidden="true" />
                {t('login.compliance')}
              </span>
              <Link to="/register" className="transition-opacity hover:opacity-75">
                {t('register.submit')}
              </Link>
            </div>
          </form>

          {/* Dev quick-fill — only on the `vite` dev server (stripped from any build). */}
          {import.meta.env.DEV && (
            <div className="mt-6 space-y-2 rounded-xl border border-dashed border-border bg-muted/40 p-4">
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {t('login.devAccounts')}
              </p>
              <div className="space-y-1.5">
                {[
                  { label: 'Admin', email: 'admin@kabbi.local' },
                  { label: 'Dispatcher', email: 'dispatcher@kabbi.local' },
                ].map(({ label, email: demoEmail }) => (
                  <button
                    key={demoEmail}
                    type="button"
                    onClick={(e) => fillDemo(e, demoEmail)}
                    className="flex w-full items-center justify-between rounded-md bg-accent/60 px-3 py-2 text-xs transition-colors hover:bg-accent"
                  >
                    <span className="font-medium text-foreground">{label}</span>
                    <span className="text-muted-foreground">{demoEmail}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* ── RIGHT: decorative live map panel (hidden on small screens) ── */}
      <AuthMapPanel />

      {/* Confirm dialog when another session is already active for this user.
          Backend returns 409 OTHER_SESSION_ACTIVE on the first login attempt;
          confirming here re-submits with force=true which drops the existing
          sessions on the server. */}
      <AlertDialog
        open={otherSessionPrompt !== null}
        onOpenChange={(open) => { if (!open) setOtherSessionPrompt(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader className="flex-row items-start gap-3 space-y-0 text-left">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <MonitorSmartphone className="h-4 w-4" />
            </span>
            <div className="min-w-0 space-y-1">
              <AlertDialogTitle>{t('login.otherSessionTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('login.otherSessionDesc', { count: otherSessionPrompt?.activeSessions ?? 0 })}
              </AlertDialogDescription>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('login.otherSessionKeep')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleForceLogin}>
              {t('login.otherSessionTerminate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
